import React, { useState, useEffect } from "react"
import { Col, Card, CardBody, Row, Badge, Button } from "reactstrap"
import api from "../../services/api"
import Swal from "sweetalert2"
import AuthService from "../../services/Auth.service"

const Vistorias = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterCondominio, setFilterCondominio] = useState("")
  const user = AuthService.getCurrentUser()

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get("/empresa/")
      const condominios = response.data

      const withDates = await Promise.all(
        condominios.map(async (item) => {
          try {
            const dateResponse = await api.get(
              `/empresa/installation-date/${item.OID_EMPRESA}`
            )
            const dateData = Array.isArray(dateResponse.data)
              ? dateResponse.data[0]
              : dateResponse.data
            return {
              id: item.OID_EMPRESA,
              razao: item.NM_RAZAO_SOCIAL,
              cnpj: item.NR_CNPJ,
              endereco: `${item.DS_LOGRADOURO || ""}, ${item.NR_LOGRADOURO || ""}, ${item.DS_BAIRRO || ""}.`,
              ultimaVistoria: dateData?.DT_ULT_VISTORIA || null,
            }
          } catch {
            return {
              id: item.OID_EMPRESA,
              razao: item.NM_RAZAO_SOCIAL,
              cnpj: item.NR_CNPJ,
              endereco: `${item.DS_LOGRADOURO || ""}, ${item.NR_LOGRADOURO || ""}, ${item.DS_BAIRRO || ""}.`,
              ultimaVistoria: null,
            }
          }
        })
      )

      setData(withDates)
    } catch (error) {
      console.error("Erro ao carregar dados de vistorias:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // ── Helpers ──

  const formatDate = (value) => {
    if (!value) return "Nunca realizada"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "Nunca realizada"
    return date.toLocaleDateString("pt-BR")
  }

  const calculatePrazo = (value) => {
    if (!value) return { text: "Sem vistoria", status: "danger", days: -Infinity }

    const date = new Date(value)
    if (Number.isNaN(date.getTime()))
      return { text: "Erro na data", status: "danger", days: -Infinity }

    const prazoDate = new Date(date)
    prazoDate.setMonth(prazoDate.getMonth() + 6)

    const hoje = new Date()
    const diffTime = prazoDate - hoje
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return {
        text: `Vencida! (${Math.abs(diffDays)} dias)`,
        status: "danger",
        days: diffDays,
      }
    } else if (diffDays <= 30) {
      return {
        text: `Atenção: faltam ${diffDays} dias`,
        status: "warning",
        days: diffDays,
      }
    } else {
      return {
        text: `Faltam ${diffDays} dias`,
        status: "success",
        days: diffDays,
      }
    }
  }

  const handleMarcarVistoria = async (item) => {
    try {
      const result = await Swal.fire({
        title: "Vistoria Realizada",
        text: `Deseja registrar a vistoria para o condomínio ${item.razao}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, marcar",
        cancelButtonText: "Cancelar",
      })

      if (result.isConfirmed) {
        const dateToSave = new Date().toISOString()
        await api.post(`/empresa/vistoria-date/${item.id}`, {
          DT_ULT_VISTORIA: dateToSave,
        })

        Swal.fire({
          icon: "success",
          title: "Vistoria marcada!",
          text: "Prazo atualizado com sucesso.",
        })

        // Atualiza estado local sem reload
        setData((prev) =>
          prev.map((d) =>
            d.id === item.id ? { ...d, ultimaVistoria: dateToSave } : d
          )
        )
      }
    } catch (e) {
      console.error("Erro ao marcar vistoria:", e)
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível marcar a vistoria.",
      })
    }
  }

  // ── Computed values ──

  const normalizeString = (str) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()

  const filteredData =
    filterCondominio.length > 0
      ? data.filter((d) =>
        normalizeString(d.razao).includes(normalizeString(filterCondominio))
      )
      : data

  const vistoriasNecessarias = data.filter((d) => {
    const prazo = calculatePrazo(d.ultimaVistoria)
    return prazo.days < 0 || d.ultimaVistoria === null
  }).length

  const vistoriasAtencao = data.filter((d) => {
    const prazo = calculatePrazo(d.ultimaVistoria)
    return prazo.days >= 0 && prazo.days <= 30
  }).length

  const vistoriasOk = data.filter((d) => {
    const prazo = calculatePrazo(d.ultimaVistoria)
    return prazo.days > 30
  }).length

  // ── Render ──

  return (
    <React.Fragment>
      <div className="page-content">
        <label id="component-title">
          Gerenciamento de Vistorias | Syskeeper
        </label>

        {/* Summary Cards */}
        <Row className="mb-3" style={{ marginTop: "20px" }}>
          <Col md={4}>
            <Card
              style={{
                borderLeft: "4px solid #dc3545",
                marginBottom: "10px",
              }}
            >
              <CardBody className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Vistorias Necessárias</h6>
                  <h3 className="mb-0" style={{ color: "#dc3545" }}>
                    {loading ? "..." : vistoriasNecessarias}
                  </h3>
                </div>
                <i
                  className="bx bx-error-circle"
                  style={{ fontSize: "32px", color: "#dc3545" }}
                />
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card
              style={{
                borderLeft: "4px solid #ffc107",
                marginBottom: "10px",
              }}
            >
              <CardBody className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Atenção (≤ 30 dias)</h6>
                  <h3 className="mb-0" style={{ color: "#ffc107" }}>
                    {loading ? "..." : vistoriasAtencao}
                  </h3>
                </div>
                <i
                  className="bx bx-time-five"
                  style={{ fontSize: "32px", color: "#ffc107" }}
                />
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card
              style={{
                borderLeft: "4px solid #28a745",
                marginBottom: "10px",
              }}
            >
              <CardBody className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Em dia</h6>
                  <h3 className="mb-0" style={{ color: "#28a745" }}>
                    {loading ? "..." : vistoriasOk}
                  </h3>
                </div>
                <i
                  className="bx bx-check-circle"
                  style={{ fontSize: "32px", color: "#28a745" }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filter */}
        <form className="component-form container" style={{ margin: "0px" }}>
          <div className="container">
            <Row>
              <label className="col-md-2 col-form-label">Condomínio</label>
              <div className="col-md-4">
                <input
                  className="form-control"
                  placeholder="Digite para procurar..."
                  onChange={(e) => setFilterCondominio(e.target.value)}
                  value={filterCondominio}
                />
              </div>
              {filterCondominio && (
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setFilterCondominio("")}
                  >
                    Limpar
                  </button>
                </div>
              )}
            </Row>
          </div>
        </form>

        {/* Table */}
        <div style={{ overflowX: "auto", marginTop: "15px" }}>
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="align-middle">Razão Social</th>
                <th className="align-middle">CNPJ</th>
                <th className="align-middle">Endereço</th>
                <th className="align-middle">Última Vistoria</th>
                <th className="align-middle">Prazo Restante</th>
                <th className="align-middle" style={{ textAlign: "center" }}>
                  Ação
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "30px" }}>
                    Carregando...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "30px" }}>
                    Nenhum condomínio encontrado.
                  </td>
                </tr>
              ) : (
                filteredData.map((item, idx) => {
                  const prazo = calculatePrazo(item.ultimaVistoria)
                  return (
                    <tr key={item.id || idx}>
                      <td>{item.razao}</td>
                      <td>{item.cnpj || "—"}</td>
                      <td>{item.endereco}</td>
                      <td>{formatDate(item.ultimaVistoria)}</td>
                      <td>
                        <Badge
                          color={prazo.status}
                          style={{
                            fontSize: "12px",
                            padding: "5px 10px",
                          }}
                        >
                          {prazo.text}
                        </Badge>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          color="primary"
                          size="sm"
                          className="btn-rounded"
                          onClick={() => handleMarcarVistoria(item)}
                        >
                          <i className="bx bx-check me-1" />
                          Vistoria Realizada
                        </Button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Vistorias
